import GSAP from 'gsap';
import { ScrollTrigger } from "gsap/ScrollTrigger";import * as THREE from "three";
import { useRef, useCallback, useState, useEffect } from "react";
import { Canvas, useLoader, useFrame, useThree } from "@react-three/fiber";
import { Bloom, EffectComposer, LUT } from "@react-three/postprocessing";
import { LUTCubeLoader } from "postprocessing";
import { Beam } from "./components/Beam";
import { Rainbow } from "./components/Rainbow";
import { Prism } from "./components/Prism";
import { Flare } from "./components/Flare";
import { Box } from "./components/Box";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { Hero } from './sections/Hero';
import { Info } from './sections/Info';
import { Featured } from './sections/Featured';
import { Cases } from './sections/Cases';
import { Founders } from './sections/Founders';
import { Menu } from './sections/Menu';
import { Intro } from './sections/Intro';
import { StickyContainer } from './sections/StickyContainer';
import { motion } from 'framer-motion';
import { useLocation } from "react-router-dom";

GSAP.registerPlugin(ScrollTrigger);

export function lerp(object, prop, goal, speed = 0.1) {
  object[prop] = THREE.MathUtils.lerp(object[prop], goal, speed);
}

const vector = new THREE.Vector3();
export function lerpV3(value, goal, speed = 0.1) {
  value.lerp(vector.set(...goal), speed);
}

export function calculateRefractionAngle(
  incidentAngle,
  glassIor = 2.5,
  airIor = 1.000293
) {
  const theta = Math.asin((airIor * Math.sin(incidentAngle)) / glassIor) || 0;
  return theta;
}

const CameraController = () => {
  const { camera, gl } = useThree();

  useEffect(() => {
    const controls = new OrbitControls(camera, gl.domElement);
    controls.minDistance = 3;
    controls.maxDistance = 20;
    return () => {
      controls.dispose();
    };
  }, [camera, gl]);

  useThree((state) => {
    state.camera?.lookAt(new THREE.Vector3(0, 0, 0))
    // state.camera.up = new THREE.Vector3(0, 0, 0);
    state.camera.updateProjectionMatrix()
  });

  return null;
};

// const CameraHelper = () => {
//   const camera = new THREE.PerspectiveCamera(60, 1, 1, 3)
//   return <group position={[2, 2, 2]}>
//     <cameraHelper args={[camera]} />
//   </group>;
// }

function activateDot(i) {
  const dots = document.querySelectorAll('.nav__menu__item');
  if (dots.length > 0){
    dots.forEach((dot) => {
      dot.classList.remove('active');
    });
    dots[i].classList.add('active');
  }
}

export default function App({...props}) {
  const texture = useLoader(
    LUTCubeLoader,
    "https://uploads.codesandbox.io/uploads/user/b3e56831-8b98-4fee-b941-0e27f39883ab/DwlG-F-6800-STD.cube"
  );

  const location = useLocation();

  useEffect(() => {

    let infoDetails = GSAP.utils.toArray('.info__item__detail');
    let infoWrapperItems = GSAP.utils.toArray('.info__wrapper__item');
    GSAP.set('.info__icon__data', {
      transformOrigin: '50% 50%',
    });
    infoDetails.forEach((detail) => {
      // Set the initial state of the panel_line
      GSAP.set(detail, {
        y: '100%',
      });
    });

    infoWrapperItems.forEach((item, i) => {
      item.addEventListener('mouseenter', () => {
        GSAP.to(infoDetails[i], {
          y: '0',
          duration: 0.5,
          ease: 'circ.out',
        });
      });
      item.addEventListener('mouseleave', () => {
        GSAP.to(infoDetails[i], {
          duration: 0.5,
          y: '100%',
        });
      })
    });

    let tl = GSAP.timeline({
      scrollTrigger: {
        trigger: '.info__wrapper',
        // markers: {startColor: "yellow", endColor: "yellow", fontSize: "12px"},
      },
    });

    // Menu dots
    GSAP.timeline({
      scrollTrigger: {trigger: '.home__message',end: '10',onEnter: () => { activateDot(0) },onEnterBack: () => { activateDot(0) } ,},
    });
    GSAP.timeline({
      scrollTrigger: {trigger: '.info__wrapper__list',end: '10',onEnter: () => { activateDot(1) },onEnterBack: () => { activateDot(1) } ,},
    });
    GSAP.timeline({
      scrollTrigger: {trigger: '.featured__copy',end: '10',onEnter: () => { activateDot(2) },onEnterBack: () => { activateDot(2) } ,},
    });
    GSAP.timeline({
      scrollTrigger: {trigger: '.cases__item',end: '10',onEnter: () => { activateDot(3) },onEnterBack: () => { activateDot(3) } ,},
    });
    GSAP.timeline({
      scrollTrigger: {trigger: '.founders__area',end: '10',onEnter: () => { activateDot(4) },onEnterBack: () => { activateDot(4) } ,},
    });

    tl.fromTo(
      ['.info__title', '.info__description', '.info__button'],
      {
        opacity: 0,
        y: -100,
      },
      {
        opacity: 1,
        y: 0,
        delay: 0.2,
      }
    );

    tl.fromTo(
      '.info__wrapper__list',
      {
        opacity: 0,
      },
      {
        opacity: 1,
      }
    );

    tl.add('icons')
    .to('.info__icon__design', 0.1, {duration: 1, x:"+=20", yoyo:true, repeat:5}, 'icons')
    .to('.info__icon__data', {duration: 1, rotation: 360}, 'icons')
    .from('.info__icon__start', {duration: 1, scale: 0}, 'icons');

  }, []);

  return (
    <Hero>
      <Canvas
        orthographic
        gl={{ antialias: false }}
        camera={{ position: [0, 0, 100], zoom: 70 }}
      >
        <color attach="background" args={["black"]} />
        <Scene />
        <EffectComposer disableNormalPass>
          <Bloom
            mipmapBlur
            levels={9}
            intensity={1.5}
            luminanceThreshold={1}
            luminanceSmoothing={1}
          />
          <LUT lut={texture} />
        </EffectComposer>
      </Canvas>
    </Hero>
  );
}

function Scene() {
  const [isPrismHit, hitPrism] = useState(true);
  const flare = useRef(null);
  const ambient = useRef(null);
  const spot = useRef(null);
  const boxreflect = useRef(null);
  const rainbow = useRef(null);

  const rayOut = useCallback(() => hitPrism(false), []);
  const rayOver = useCallback((e) => {
    // Break raycast so the ray stops when it touches the prism.
    e.stopPropagation();
    hitPrism(true);
    // Set the intensity really high on first contact.
    rainbow.current.material.speed = 1;
    rainbow.current.material.emissiveIntensity = 5;
  }, []);

  const vec = new THREE.Vector3();
  const rayMove = useCallback(({ api, position, direction, normal }) => {
    if (!normal) return;
    // Extend the line to the prisms center.
    vec.toArray(api.positions, api.number++ * 3);
    // Set flare.
    flare.current.position.set(position.x, position.y, -0.5);
    // flare.current.rotation.set(-0, 0, -Math.atan2(direction.x, direction.y));
    flare.current.rotation.set(-0, 0, -Math.atan2(direction.x, direction.y));

    // Calculate refraction angles.
    let angleScreenCenter = Math.atan2(-position.y, -position.x);
    const normalAngle = Math.atan2(normal.y, normal.x);

    // The angle between the ray and the normal.
    const incidentAngle = angleScreenCenter - normalAngle;

    // Calculate the refraction for the incident angle.
    const refractionAngle = calculateRefractionAngle(incidentAngle) * 6;

    // Apply the refraction.
    angleScreenCenter += refractionAngle;
    rainbow.current.rotation.z = angleScreenCenter;

    // Set spot light.
    lerpV3(
      spot.current.target.position,
      [Math.cos(angleScreenCenter), Math.sin(angleScreenCenter), 0],
      0.05
    );
    spot.current.target.updateMatrixWorld();
  }, []);

  useFrame((state) => {

    // const hero = document.querySelector('.viewport__container__hero')
    // const info = document.querySelector('.viewport__container__info')
    // if (hero){
    //   var rectHero = hero.getBoundingClientRect();
    //   var rectInfo = info.getBoundingClientRect();
    //   var viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
    //   if (!(rectInfo.bottom < 0 || rectHero.top - viewHeight >= 0)) {
    //     state.setFrameloop('always');
    //   } else {
    //     state.setFrameloop('demand');
    //   }
    // }

    // const clients = document.querySelector('.viewport__container__cases')
    // const founders = document.querySelector('.viewport__container__founders')
    // if (clients){
    //   var rectClients = clients.getBoundingClientRect();
    //   var rectFounders = founders.getBoundingClientRect();
    //   var viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
    //   if (rectClients.top < 0) {
    //     state.setFrameloop('always');
    //   } else {
    //     state.setFrameloop('demand');
    //   }
    // }


    // Tie beam to the mouse.
    boxreflect.current.setRay(
      [
        (state.pointer.x * state.viewport.width) / 2,
        (state.pointer.y * state.viewport.height) / 2,
        0
      ],
      [0, 0, 0]
    );

    // Rotate rainbow.
    let angleScreenCenter = Math.atan2(-50, 60);
    rainbow.current.rotation.z = angleScreenCenter;


    // Animate rainbow intensity.
    lerp(
      rainbow.current.material,
      "emissiveIntensity",
      isPrismHit ? 2.5 : 0,
      0.1
    );
    spot.current.intensity = rainbow.current.material.emissiveIntensity;

    // Animate ambience.
    lerp(ambient.current, "intensity", 0, 0.025);
  });

  return (
    <>
      {/* Lights */}
      <ambientLight ref={ambient} intensity={0} />
      <pointLight position={[10, -10, 0]} intensity={0.05} />
      <pointLight position={[0, 10, 0]} intensity={0.05} />
      <pointLight position={[-10, 0, 0]} intensity={0.05} />
      <spotLight
        ref={spot}
        intensity={1}
        distance={7}
        angle={1}
        penumbra={1}
        position={[0, 0, 1]}
      />
      {/* Prism + blocks + reflect beam */}
      <Beam ref={boxreflect} bounce={10} far={20}>
        <Prism
          scale={0.6}
          position={[0, -0.5, 0]}
          onRayOver={rayOver}
          onRayOut={rayOut}
          onRayMove={rayMove}
        />
        <Box visible={false} position={[-1.4, 1, 0]} rotation={[0, 0, Math.PI / 8]} />
        <Box visible={false} position={[-2.4, -1, 0]} rotation={[0, 0, Math.PI / -4]} />
      </Beam>
      {/* Rainbow and flares */}
      <Rainbow ref={rainbow} startRadius={0} endRadius={0.5} fade={0} />
      <Flare
        ref={flare}
        visible={isPrismHit}
        renderOrder={10}
        scale={1.25}
        streak={[12.5, 20, 1]}
      />
    </>
  );
}
